import type { Program } from '@/types/program';
import { Pill, Typography } from '@/ui';
import { cn } from '@/ui/lib/utils';
import isDualDiagnosisProgram from '@/utils/isDualDiagnosisProgram';
import snakeToTitleCase from '@/utils/snakeToTitleCase';

import './styles.css';
import type { Props } from './types';

type RowState = 'unchanged' | 'expected' | 'unexpected' | 'new' | 'flagged';

interface SummaryRow {
  label: string;
  from: string | null;
  to: string;
  state: RowState;
}

const track = (program: Program) => (isDualDiagnosisProgram(program.name) ? 'Dual' : 'Standard');
const levelOfCare = (program: Program) => snakeToTitleCase(program.phase_type_name);
const primaryConcern = (program: Program) => snakeToTitleCase(program.drug_category);

const WARNING_ROW = 'rounded bg-yellow-50 font-semibold dark:bg-yellow-950';
const WARNING_PILL: Partial<Record<RowState, string>> = {
  unexpected: 'Changing',
  flagged: 'Confirm'
};

/**
 * Splits a program into the parts clinicians reason about, so a level of care
 * change reads differently from an accidental change of track or concern.
 * Only those accidental-looking changes get color.
 */
const summaryRows = (fromProgram: Program | null, toProgram: Program): SummaryRow[] => {
  const dimensions = [
    { label: 'Level of care', value: levelOfCare, changeIsExpected: true },
    { label: 'Track', value: track, changeIsExpected: false },
    { label: 'Primary concern', value: primaryConcern, changeIsExpected: false }
  ];

  return dimensions.map(({ label, value, changeIsExpected }) => {
    const to = value(toProgram);
    if (!fromProgram) {
      const flagged = label === 'Track' && to === 'Dual';
      return { label, from: null, to, state: flagged ? 'flagged' : 'new' };
    }
    const from = value(fromProgram);
    let state: RowState = 'unchanged';
    if (from !== to) state = changeIsExpected ? 'expected' : 'unexpected';
    return { label, from, to, state };
  });
};

const ProgramChangeSummary = ({ fromProgram, toProgram }: Props) => {
  const rows = summaryRows(fromProgram, toProgram);

  return (
    <div data-testid="program-change-summary">
      <dl className="flex flex-col gap-1 text-sm">
        {rows.map((row) => {
          const warningPill = WARNING_PILL[row.state];
          return (
            <div
              key={row.label}
              role="group"
              aria-label={row.label}
              data-state={row.state}
              className={cn(
                'flex items-center gap-3 px-2 py-1',
                warningPill && WARNING_ROW,
                row.state === 'unchanged' && 'text-gray-500 dark:text-gray-400'
              )}>
              <dt className="w-32 shrink-0">{row.label}</dt>
              <dd className="flex flex-1 flex-wrap items-center justify-between gap-2">
                <span>
                  {row.from !== null && row.from !== row.to ? `${row.from} → ${row.to}` : row.to}
                  {row.state === 'unchanged' && <span className="ml-1">(no change)</span>}
                </span>
                {warningPill && (
                  <Pill color="warning" size="sm">
                    {warningPill}
                  </Pill>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      <Typography variant="caption" as="p" muted className="mt-1 px-2">
        {fromProgram ? `${fromProgram.name} → ` : ''}
        {toProgram.name}
      </Typography>
    </div>
  );
};

export default ProgramChangeSummary;
